import { React, useEffect, useState } from "react";
import cogoToast from "cogo-toast";
import { useHistory, Link } from "react-router-dom";
import { Breadcrumb, BreadcrumbItem, Table, Button, Modal, ModalHeader, ModalBody, ModalFooter, Spinner } from "reactstrap";
import { genericGet,genericPost } from "../../services/Api";
import { ENDPOINTS } from "../../Constants";
import posPrinterService from "../../services/PosPrinter";
import excelExportService from "../../services/ExcelExport";

const RedemptionReport = (props) => {
  const history = useHistory();
 
  const [loading, setLoading] = useState(false);
  const [cycle_id, setCycle_id] = useState(null);
  const [rowData, setRowData] = useState([]);
  const [active_cycles, setActive_cycles] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedEntitlement, setSelectedEntitlement] = useState(null);
  const [redemptionData, setRedemptionData] = useState({
    status: 0,
    message: "",
    data: []
  });

  const [centers, setCenters] = useState([]);
  const [selectedCenter, setSelectedCenter] = useState(null);
  const [fromDate, setFromDate] = useState("2025-01-01");
  const [toDate, setToDate] = useState("2025-12-31");
  const [printingReceipt, setPrintingReceipt] = useState(null);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [schemes, setSchemes] = useState([]);
  const [selectedScheme, setSelectedScheme] = useState('');
  const [modalError, setModalError] = useState('');

  const toggleModal = () => {
    setIsModalOpen(!isModalOpen);
    if (!isModalOpen) {
      setModalError(''); // Clear error when opening modal
    }
  };

  const handleViewReceipt = (entitlement, record) => {
    setSelectedEntitlement(entitlement);
    setCurrentRecord(record);
    toggleModal();
  };

  // Handle POS receipt printing
  const handlePrintReceipt = async () => {
    if (!selectedEntitlement || !currentRecord) {
      setModalError('No receipt data available');
      return;
    }

    try {
      setModalError(''); // Clear any previous errors
      setPrintingReceipt(currentRecord.receipt_no);
      
      // Prepare receipt data
      const receiptData = {
        patientName: currentRecord.patient_name,
        receiptNo: currentRecord.receipt_no,
        doctorName: currentRecord.doctor_name,
        prescriptionDate: currentRecord.prescription_issued_on,
        items: selectedEntitlement,
        totalAmount: parseFloat(currentRecord.irs_redeemed_amount || 0) + parseFloat(currentRecord.bnf_cash || 0),
        irsAmount: currentRecord.irs_redeemed_amount,
        beneficiaryCash: currentRecord.bnf_cash,
        hospitalName: currentRecord.hospital_name
      };

      // Facility info
      const facilityInfo = {
        name: centers.find(c => c.id === selectedCenter)?.center_name || 'Healthcare Facility',
        address: 'Healthcare Management System',
        phone: 'Contact: +1-234-567-8900'
      };

      // Print the receipt
      const result = await posPrinterService.printReceipt(receiptData, facilityInfo);
      
      if (result.success) {
        // Mark receipt as printed in local storage
        posPrinterService.markReceiptAsPrinted(currentRecord.receipt_no, receiptData);
        
        cogoToast.success(`Receipt printed successfully via ${result.method} method`);
        
        // Refresh the data to show updated print status
        fetchRedemptionData();
      } else {
        setModalError(result.error || 'Failed to print receipt');
      }
      
    } catch (error) {
      console.error('Print error:', error);
      setModalError('Failed to print receipt: ' + error.message);
    } finally {
      setPrintingReceipt(null);
    }
  };

  // Handle Excel export
  const handleExportExcel = async () => {
    if (!displayData.data || displayData.data.length === 0) {
      cogoToast.error('No data to export');
      return;
    }

    try {
      setExportingExcel(true);
      
      const filters = {
        centerName: centers.find(c => c.id === selectedCenter)?.center_name || 'All Centers',
        fromDate: fromDate,
        toDate: toDate
      };

      const result = excelExportService.exportDetailedRedemptionReport(displayData.data, filters);
      
      if (result.success) {
        cogoToast.success('Excel file exported successfully');
      } else {
        cogoToast.error('Failed to export Excel file: ' + result.message);
      }
    } catch (error) {
      console.error('Excel export error:', error);
      cogoToast.error('Failed to export Excel file: ' + error.message);
    } finally {
      setExportingExcel(false);
    }
  };

  // Utility function to convert images to base64 for printing
  const convertImagesToBase64 = (htmlContent) => {
    return new Promise((resolve) => {
      const tempDiv = document.createElement('div');
      tempDiv.innerHTML = htmlContent;
      const images = tempDiv.querySelectorAll('img');
      
      if (images.length === 0) {
        resolve(htmlContent);
        return;
      }
      
      let processedImages = 0;
      const totalImages = images.length;
      
      images.forEach((img, index) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const tempImg = new Image();
        
        tempImg.crossOrigin = 'anonymous';
        tempImg.onload = function() {
          canvas.width = tempImg.width;
          canvas.height = tempImg.height;
          ctx.drawImage(tempImg, 0, 0);
          
          try {
            const dataURL = canvas.toDataURL('image/png');
            img.src = dataURL;
          } catch (error) {
            console.warn('Could not convert image to base64:', error);
            // Replace with placeholder if conversion fails
            img.outerHTML = '<div style="width: 120px; height: 80px; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; background: #f8f9fa;">LOGO</div>';
          }
          
          processedImages++;
          if (processedImages === totalImages) {
            resolve(tempDiv.innerHTML);
          }
        };
        
        tempImg.onerror = function() {
          console.warn('Could not load image for printing:', img.src);
          // Replace with placeholder if image fails to load
          img.outerHTML = '<div style="width: 120px; height: 80px; border: 1px solid #ccc; display: flex; align-items: center; justify-content: center; background: #f8f9fa;">LOGO</div>';
          
          processedImages++;
          if (processedImages === totalImages) {
            resolve(tempDiv.innerHTML);
          }
        };
        
        tempImg.src = img.src;
      });
    });
  };

  // Print functionality
  const handlePrint = async () => {
    const printContent = document.getElementById('print-content');
    
    // Create print-specific content
    const printWindow = window.open('', '_blank');
    
    // Get current domain for relative image paths
    const currentDomain = window.location.origin;
    
    // Process images in the content
    const processedContent = printContent ? await convertImagesToBase64(printContent.innerHTML) : '';
    
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title> REDEMPTION REPORT - ${centers.find(c => c.id === selectedCenter)?.center_name || 'N/A'}</title>
          <style>
            @media print {
              body { 
                font-family: Arial, sans-serif; 
                margin: 20px; 
                -webkit-print-color-adjust: exact;
                print-color-adjust: exact;
              }
              .print-header { 
                margin-bottom: 30px; 
                border-bottom: 2px solid #333; 
                padding-bottom: 20px; 
              }
              .header-content { 
                display: flex; 
                justify-content: space-between; 
                align-items: flex-start; 
              }
              .header-left { 
                flex: 1; 
                text-align: left; 
              }
              .header-right { 
                flex: 0 0 auto; 
                text-align: right; 
                min-height: 80px; 
                display: flex; 
                align-items: center; 
                justify-content: flex-end; 
              }
              .print-title { 
                font-size: 24px; 
                font-weight: bold; 
                margin-bottom: 10px; 
              }
              .print-subtitle { 
                font-size: 14px; 
                color: #666; 
              }
              .print-date { 
                font-size: 12px; 
                color: #999; 
                margin-top: 10px; 
              }
              .print-filters { 
                margin-top: 15px; 
                text-align: left; 
                padding: 15px; 
                background-color: #f8f9fa; 
                border-radius: 5px; 
              }
              .filter-row { 
                margin-bottom: 8px; 
                font-size: 13px; 
              }
              .filter-row strong { 
                color: #333; 
              }
              .print-logo { 
                max-width: 120px; 
                max-height: 80px; 
                object-fit: contain; 
                border: 1px solid #ddd; 
              }
              .logo-placeholder {
                width: 120px;
                height: 80px;
                border: 2px solid #333;
                display: flex;
                align-items: center;
                justify-content: center;
                font-size: 12px;
                color: #666;
                background-color: #f8f9fa;
              }
              table { 
                width: 100%; 
                border-collapse: collapse; 
                margin-top: 20px; 
              }
              th, td { 
                border: 1px solid #ddd; 
                padding: 8px; 
                text-align: left; 
                font-size: 12px; 
              }
              th { 
                background-color: #f8f9fa; 
                font-weight: bold; 
              }
              .print-summary { 
                margin-top: 30px; 
                padding: 15px; 
                background-color: #f8f9fa; 
                border-radius: 5px; 
              }
              .print-summary h4 { 
                margin-bottom: 10px; 
              }
              .no-print { 
                display: none; 
              }
              @page { 
                margin: 1cm; 
              }
            }
            @media screen {
              .print-only { 
                display: none; 
              }
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div class="header-content">
              <div class="header-left">
                <div class="print-title">Redemption Report</div>
                <div class="print-subtitle">HMIS - Healthcare Management Information System</div>
                <!-- <div class="print-date">Generated on: ${new Date().toLocaleDateString()} at ${new Date().toLocaleTimeString()}</div> -->
                <div class="print-filters">
                  <div class="filter-row">
                    <strong>Service Center:</strong> ${centers.find(c => c.id === selectedCenter)?.center_name || 'N/A'}
                  </div>
                  <div class="filter-row">
                    <strong>From Date:</strong> ${fromDate} | <strong>To Date:</strong> ${toDate}
                  </div>
                  <div class="filter-row">
                    <strong>Scheme:</strong> ${displayData.data[0]?.scheme || 'N/A'}
                  </div>
                </div>
              </div>
              <div class="header-right">
                <div class="logo-placeholder">
                  <img src="${currentDomain}/images/irs_logo.png" 
                       alt="Logo" 
                       style="max-width: 100%; max-height: 100%; object-fit: contain;"
                       onerror="this.style.display='none'; this.nextElementSibling.style.display='flex';">
                  <div style="display: none; width: 100%; height: 100%; align-items: center; justify-content: center; font-size: 12px; color: #666;">
                    LOGO
                  </div>
                </div>
              </div>
            </div>
          </div>
          ${processedContent}
          <div class="print-summary">
            <h4>Report Summary</h4>
            <p><strong>Total Records:</strong> ${displayData.data.length}</p>
            <p><strong>Total IRS Amount:</strong> $${displayData.data.reduce((sum, record) => sum + parseFloat(record.irs_redeemed_amount || 0), 0).toFixed(2)}</p>
            <p><strong>Total Beneficiary Cash:</strong> $${displayData.data.reduce((sum, record) => sum + parseFloat(record.bnf_cash || 0), 0).toFixed(2)}</p>
            <p><strong>Grand Total:</strong> $${(displayData.data.reduce((sum, record) => sum + parseFloat(record.irs_redeemed_amount || 0), 0) + displayData.data.reduce((sum, record) => sum + parseFloat(record.bnf_cash || 0), 0)).toFixed(2)}</p>
          </div>
        </body>
      </html>
    `);
    
    printWindow.document.close();
    
    // Wait for content to load before printing
    printWindow.onload = function() {
      setTimeout(() => {
        printWindow.focus();
        printWindow.print();
        printWindow.close();
      }, 500);
    };
  };

     // Fetch redemption data from API
   const fetchRedemptionData = async () => {
     try {
       setLoading(true);
       // Replace this with your actual API endpoint
       const response = await genericPost(ENDPOINTS.REDEEMPTIONS_PER_CENTER,{
         "center_id": selectedCenter,
         "from_date": fromDate,
         "to_date": toDate,
         "cycle_id": selectedScheme
     });
       
       if (response && response.data.status === 10001) {
         setRedemptionData(response.data);
       } else {
         cogoToast.error(response?.message || 'Failed to fetch redemption data');
       }
     } catch (error) {
       console.error('Error fetching redemption data:', error);
       cogoToast.error('Failed to fetch redemption data');
     } finally {
       setLoading(false);
     }
   };
  const listCenters = async () => {
    try {
      setLoading(true);
      // Replace this with your actual API endpoint
      const response = await genericGet(ENDPOINTS.LIST_CENTERS);
      
      if (response && response.data.status === 10001) {
        setCenters(response.data.data);
      } else {
        cogoToast.error(response?.message || 'Failed to fetch redemption data');
      }
    } catch (error) {
      console.error('Error fetching centers data:', error);
      cogoToast.error('Failed to fetch centers list');
    } finally {
      setLoading(false);
    }
  };

  const loadSchemes = async () => {
    try {
      setLoading(true);
      const response = await genericGet(ENDPOINTS.GET_ACTIVE_CYCLES);
      
      if (response && response.data.status === 10001) {
        setSchemes(response.data.data);
        if (response.data.data.length > 0) {
          setSelectedScheme(response.data.data[0].cycle_id);
        }
      } else {
        cogoToast.error(response?.message || 'Failed to fetch schemes data');
      }
    } catch (error) {
      console.error('Error fetching schemes data:', error);
      cogoToast.error('Failed to fetch schemes list');
    } finally {
      setLoading(false);
    }
  };
     useEffect(() => {
     listCenters();
     loadSchemes();
 
     return () => {};
   }, []);

   // Refetch data when center or dates change
   useEffect(() => {
     if (centers.length > 0 && selectedCenter) {
       fetchRedemptionData();
     }
   }, [selectedCenter, fromDate, toDate]);

  // Mock data for development - remove this in production
  const mockRedemptionData = {
    "status": 10001,
    "message": "Your request was processed successfully.",
    "data": [
     
    ]
  };

  // Use mock data for development - replace with redemptionData in production
  const displayData = redemptionData.data.length > 0 ? redemptionData : mockRedemptionData;

  return (
    <div className="page-wrapper">
             <style>
         {`
                       /* Display styles - smaller font for better column fit */
            .table { font-size: 12px !important; }
            .table th, .table td { padding: 6px 8px !important; }
            .table th { font-size: 11px !important; font-weight: bold; }
            .container-xl { max-width: 95% !important; }
            .table-responsive { overflow-x: auto; }
            .table th, .table td { 
              white-space: nowrap; 
              overflow: hidden; 
              text-overflow: ellipsis; 
              max-width: 0;
            }
            .table td { word-wrap: break-word; }
           
           @media print {
             .no-print, .card-actions, .breadcrumb, .page-header, .modal { display: none !important; }
             .card { border: none !important; box-shadow: none !important; }
             .card-header { background: none !important; border-bottom: 2px solid #333 !important; }
             .card-body { padding: 0 !important; }
             .table { font-size: 11px !important; }
             .table th, .table td { padding: 4px 6px !important; }
             body { margin: 0 !important; padding: 20px !important; }
             .page-wrapper { margin: 0 !important; }
             .container-xl { max-width: none !important; }
           }
         `}
       </style>
      <div className="page-bodyxx">
        <div className="container-xlx">
          {/* Breadcrumb */}
          <div className="page-header d-print-none">
            <div className="container-xl">
              <div className="row ">
                <div className="col">
                  
                  <h2 className="page-title">Redemption Report</h2>
                </div>
              </div>
              <div className="row g-2 align-items-center">
                <div className="col">
                  <Breadcrumb>
                    <BreadcrumbItem>
                      <Link to="/">Home</Link>
                    </BreadcrumbItem>
                    <BreadcrumbItem>
                      <Link to="/hmis">HMIS</Link>
                    </BreadcrumbItem>
                    <BreadcrumbItem active>Redemption Report</BreadcrumbItem>
                    
                  </Breadcrumb>
                </div>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="row row-cards">
            <div className="col-12">
              <div className="card">
                                 <div className="card-header">
                   <h3 className="card-title">Redemption Report</h3>
                   <div className="card-actions">
                     <Button 
                       color="success" 
                       onClick={handlePrint}
                       className="me-2"
                       disabled={loading}
                     >
                       <i className="ti ti-printer me-1"></i>
                       Print Report
                     </Button>
                     <Button 
                       color="info" 
                       onClick={handleExportExcel}
                       className="me-2"
                       disabled={loading || exportingExcel || !displayData.data || displayData.data.length === 0}
                     >
                       {exportingExcel ? (
                         <>
                           <Spinner size="sm" className="me-1" />
                           Exporting...
                         </>
                       ) : (
                         <>
                           <i className="ti ti-file-spreadsheet me-1"></i>
                           Export Excel
                         </>
                       )}
                     </Button>
                     <Button 
                       color="primary" 
                       onClick={fetchRedemptionData}
                       disabled={loading}
                       className="me-2"
                     >
                       {loading ? <Spinner size="sm" /> : 'Refresh'}
                     </Button>
                     {/* <Button 
                       color="warning" 
                       onClick={() => {
                         if (window.confirm('Are you sure you want to clear all printed receipt records? This action cannot be undone.')) {
                           posPrinterService.clearPrintedReceipts();
                           cogoToast.success('Printed receipt records cleared');
                           fetchRedemptionData();
                         }
                       }}
                       disabled={loading}
                     >
                       <i className="ti ti-trash me-1"></i>
                       Clear Print History
                     </Button> */}
                   </div>
                 </div>
                 
                 {/* Filters */}
                 <div className="card-body border-bottom">
                   <div className="row g-3 align-items-center">
                  
                  
                  
                     <div className="col-md-4">
                       <label className="form-label fw-bold">Service Center:</label>
                       <select 
                         className="form-select" 
                         value={selectedCenter} 
                         onChange={(e) => setSelectedCenter(e.target.value)}
                         disabled={loading}
                       >
                         {centers.map((center) => (
                           <option key={center.id} value={center.id}>
                             {center.center_name}
                           </option>
                         ))}
                       </select>


                     </div>

                     <div className="col-md-3">
                       <label className="form-label fw-bold">Scheme:</label>
                       <select 
                         className="form-select" 
                         value={selectedScheme} 
                         onChange={(e) => setSelectedScheme(e.target.value)}
                         disabled={loading}
                       >
                         {schemes.map((scheme) => (
                           <option key={scheme.cycle_id} value={scheme.cycle_id}>
                             {scheme.cycle_name}
                           </option>
                         ))}
                         
                       </select>
                     </div>


                     <div className="col-md-3">
                       <label className="form-label fw-bold">From Date:</label>
                       <input 
                         type="date" 
                         className="form-control" 
                         value={fromDate} 
                         onChange={(e) => setFromDate(e.target.value)}
                         disabled={loading}
                       />
                     </div>



                     <div className="col-md-3">
                       <label className="form-label fw-bold">To Date:</label>
                       <input 
                         type="date" 
                         className="form-control" 
                         value={toDate} 
                         onChange={(e) => setToDate(e.target.value)}
                         disabled={loading}
                       />
                     </div>
                     <div className="col-md-2 d-flex align-items-end" style={{marginBottom: '-25px'}}>
                       <Button 
                         color="info" 
                         onClick={fetchRedemptionData}
                         disabled={loading}
                         className="w-100 me-1"
                       >
                         {loading ? <Spinner size="sm" /> : 'Apply'}
                       </Button>
                       
                     </div>
                   </div>
                 </div>
                                                                   <div className="card-body">
                      <center>
                      <div className="row">
                        <div className="col-md-6">
                          <strong>Scheme:</strong> {displayData.data[0]?.scheme || 'N/A'}
                        </div>
                        <div className="col-md-6">
                          <strong>Service Center:</strong> {centers.find(c => c.id === selectedCenter)?.center_name || 'N/A'}
                        </div>
                      </div>
                      <hr/>
                     </center>
                  {loading ? (
                    <div className="text-center py-4">
                      <Spinner size="lg" />
                      <p className="mt-2">Loading redemption data...</p>
                    </div>
                                     ) : (
                     <div id="print-content">
  
                       <div className="table-responsive">
                       <p><strong>Total Records:</strong> {displayData.data.length}</p>
                     <Table className="table table-striped table-hover">
                                                        <thead className="table-dark">
                               <tr>
                               <th style={{width: '8%'}}>S.No </th>

                               <th style={{width: '8%'}}>Receipt No</th>
                               <th style={{width: '8%'}}>Voucher No</th>
                               <th style={{width: '10%'}}>Redeemed On</th>
                                 <th style={{width: '12%'}}>Patient Name</th>
                                 <th style={{width: '10%'}}>Doctor Name</th>
                              
                          
                                 <th style={{width: '10%'}}>Prescription Date</th>
                                 {/* <th style={{width: '6%'}}>Discount</th> */}
                                
                                 <th style={{width: '12%'}}>Hospital</th>
                           
                                 <th style={{width: '8%'}}>IRS Amount</th>
                                 <th style={{width: '8%'}}>BNF Cash</th>
                               
                                 <th style={{width: '9%'}} className="no-print">Actions</th>
                               </tr>
                             </thead>
                            <tbody>
                              {displayData.data.map((record, index) => (
                                <tr key={index}>
                                  {/* <td>{record.wallet_account_name}</td> */}
                                  <td>{index + 1}</td>
                                  <td>{record.receipt_no || '-'}</td>
                                  <td>{record.voucher_no || '-'}</td>
                                  <td>{record.redeemed_on}</td>
                                  <td>{record.patient_name}</td>
                                  <td>{record.doctor_name}</td>
                             
                                  
                                  <td>{record.prescription_issued_on}</td>
                                  {/* <td>{record.discount_allowed || '-'}</td> */}
                                
                                  <td>{record.hospital_name}</td>
                                 
                                  <td className="text-end fw-bold">${record.irs_redeemed_amount}</td>
                                  <td className="text-end fw-bold">${record.bnf_cash}</td>
                                
                                  <td className="no-print">
                                    <Button
                                      color="info"
                                      size="sm"
                                      onClick={() => handleViewReceipt(record.entitlement_issued, record)}
                                      className="me-1"
                                    >
                                      View Receipt
                                    </Button>
                                  </td>
                                </tr>
                              ))}
                              {/* Subtotals Row */}
                              <tr className="table-info fw-bold">
                                <td colSpan="8" className="text-end">
                                  <strong>Subtotals:</strong>
                                </td>
                                <td className="text-end">
                                  ${displayData.data.reduce((sum, record) => sum + parseFloat(record.irs_redeemed_amount || 0), 0).toFixed(2)}
                                </td>
                                <td className="text-end">
                                  ${displayData.data.reduce((sum, record) => sum + parseFloat(record.bnf_cash || 0), 0).toFixed(2)}
                                </td>
                                
                                <td className="no-print"></td>
                                <td className="no-print"></td>
                                <td className=""></td>
                              </tr>
                            </tbody>
                         </Table>
                       </div>
                     </div>
                   )}
                </div>
              </div>
            </div>
          </div>

          {/* Modal for Entitlement Details */}
          <Modal isOpen={isModalOpen} toggle={toggleModal} size="lg">
            <ModalHeader toggle={toggleModal}>
              <h4>Entitlement Receipt Details</h4>
              {currentRecord && (
                <div className="mt-2">
                  <span className="badge bg-info me-2">
                    Receipt: {currentRecord.receipt_no}
                  </span>
               
                </div>
              )}
            </ModalHeader>
            <ModalBody>
              {selectedEntitlement && (
                <div className="table-responsive">
                  <Table className="table table-bordered">
                    <thead className="table-dark">
                      <tr>
                        <th>Item Name</th>
                        <th>Quantity</th>
                        <th>Unit Price</th>
                        <th>Total Amount</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedEntitlement.map((item, index) => (
                        <tr key={index}>
                          <td>
                            <div style={{ whiteSpace: 'pre-line' }}>
                              {item.name}
                            </div>
                          </td>
                          <td>{item.quantity}</td>
                          <td>${item.price}</td>
                          <td>${item.amount}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr className="table-info">
                        <td colSpan="3" className="text-end fw-bold">
                          Total Amount:
                        </td>
                        <td className="fw-bold">
                          ${selectedEntitlement.reduce((sum, item) => sum + parseFloat(item.amount), 0).toFixed(2)}
                        </td>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}
            </ModalBody>
            <ModalFooter>
              <div className="w-100">
                {/* Error Display */}
                {modalError && (
                  <div className="alert alert-danger mb-3" role="alert">
                    <strong>Error:</strong> {modalError}
                  </div>
                )}
                
                <div className="d-flex justify-content-end">
                  <Button 
                    color="success" 
                    onClick={handlePrintReceipt}
                    disabled={printingReceipt === currentRecord?.receipt_no}
                    className="me-2"
                  >
                    {printingReceipt === currentRecord?.receipt_no ? (
                      <>
                        <Spinner size="sm" className="me-1" />
                        Printing...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-printer me-1"></i>
                        Print Receipt
                      </>
                    )}
                  </Button>
                  <Button color="secondary" onClick={toggleModal}>
                    Close
                  </Button>
                </div>
              </div>
            </ModalFooter>
          </Modal>
        </div>
      </div>
    </div>
  );
};

export default RedemptionReport;
